import { FieldErrors } from 'react-hook-form';

/**
 * Determines if a validation error should block saving.
 * Returns true for format/pattern/business logic errors.
 * Returns false for required field errors (incomplete data).
 */
export const isBlockingSaveError = (error: any): boolean => {
  if (!error) return false;

  // Check error type based on the validation rule that failed
  if (error.type) {
    switch (error.type) {
      case 'pattern':     // Format errors (email, RUT, etc.)
      case 'validate':    // Custom validation function failures
      case 'min':         // Min value violations
      case 'max':         // Max value violations
      case 'minLength':   // Min length violations
      case 'maxLength':   // Max length violations
        return true;
      case 'required':    // Missing required fields (incomplete data)
        return false;
      default:
        return false;
    }
  }

  return false;
};

/**
 * Checks if form has validation errors that should block saving.
 * Only blocks for format/pattern/business logic errors, not incomplete data.
 */
export const hasBlockingSaveErrors = (errors: FieldErrors): boolean => {
  const checkErrorsRecursively = (errorObj: any): boolean => {
    if (!errorObj) return false;

    // If this is a single error object
    if (errorObj.type || errorObj.message) {
      return isBlockingSaveError(errorObj);
    }

    // If this is an object containing multiple errors, check recursively
    for (const key in errorObj) {
      if (errorObj.hasOwnProperty(key)) {
        const value = errorObj[key];

        // Handle arrays (like coInvestigators)
        if (Array.isArray(value)) {
          for (const item of value) {
            if (checkErrorsRecursively(item)) {
              return true;
            }
          }
        }
        // Handle nested objects
        else if (typeof value === 'object' && value !== null) {
          if (checkErrorsRecursively(value)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  return checkErrorsRecursively(errors);
};

/**
 * Gets count of blocking vs non-blocking errors for user feedback
 */
export const getErrorCounts = (errors: FieldErrors): { blocking: number; nonBlocking: number } => {
  let blocking = 0;
  let nonBlocking = 0;

  const countErrorsRecursively = (errorObj: any): void => {
    if (!errorObj) return;

    // If this is a single error object
    if (errorObj.type || errorObj.message) {
      if (isBlockingSaveError(errorObj)) {
        blocking++;
      } else {
        nonBlocking++;
      }
      return;
    }

    // If this is an object containing multiple errors, count recursively
    for (const key in errorObj) {
      if (errorObj.hasOwnProperty(key)) {
        const value = errorObj[key];

        // Handle arrays (like coInvestigators)
        if (Array.isArray(value)) {
          for (const item of value) {
            countErrorsRecursively(item);
          }
        }
        // Handle nested objects
        else if (typeof value === 'object' && value !== null) {
          countErrorsRecursively(value);
        }
      }
    }
  };

  countErrorsRecursively(errors);

  return { blocking, nonBlocking };
};